import { useState } from 'react'
import { PlusIcon } from 'lucide-react'
import { formatDecimalHours } from '@coneno/c-sheet-shared'
import { ListToolbar } from '@/components/layout/list-toolbar'
import { matchesStatus, type StatusFilter } from '@/lib/status-filter'
import { PageBody, PageHeader } from '@/components/layout/page-header'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useCustomers } from '@/hooks/use-customers'
import { useProjects } from '@/hooks/use-projects'
import { usePanelParam } from '@/hooks/use-panel-param'
import { useAllTasks } from '@/hooks/use-tasks'
import { ProjectPanel } from './project-panel'

export function ProjectsPage(): React.JSX.Element {
  const projects = useProjects()
  const customers = useCustomers()
  const tasks = useAllTasks()
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<StatusFilter>('active')
  const [panel, setPanel] = usePanelParam('project')
  const taskCounts = new Map<string, number>()
  for (const task of tasks.data ?? []) {
    taskCounts.set(task.projectId, (taskCounts.get(task.projectId) ?? 0) + 1)
  }
  const customerNames = new Map(customers.data?.map((customer) => [customer.id, customer.name]))
  const query = search.trim().toLowerCase()
  const visibleRows = (projects.data ?? []).filter(
    (row) =>
      matchesStatus(row.active, status) &&
      (!query ||
        [row.name, row.code, row.customerId ? customerNames.get(row.customerId) : 'internal']
          .filter(Boolean)
          .some((value) => value!.toLowerCase().includes(query)))
  )
  const activeCount = projects.data?.filter((project) => project.active).length ?? 0
  const inactiveCount = (projects.data?.length ?? 0) - activeCount
  const editing =
    panel && panel !== 'new' ? (projects.data?.find((p) => p.id === panel) ?? null) : null
  const error = projects.error ?? customers.error ?? tasks.error

  return (
    <PageBody>
      <PageHeader
        title="Projects"
        description={
          projects.data ? `${activeCount} active · ${inactiveCount} inactive` : 'Loading…'
        }
        actions={
          <Button size="sm" onClick={() => setPanel('new')}>
            <PlusIcon data-icon="inline-start" />
            New project
          </Button>
        }
      >
        <ListToolbar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search projects, clients, codes"
          status={status}
          onStatusChange={setStatus}
        />
      </PageHeader>

      {error && <p className="text-sm text-destructive">{error.message}</p>}

      <div className="overflow-x-auto rounded-lg border bg-card">
        <table className="w-full min-w-150 text-sm">
          <thead>
            <tr className="border-b bg-muted/40 text-xs text-muted-foreground">
              <th className="px-3 py-2 text-left font-medium">Project</th>
              <th className="px-3 py-2 text-left font-medium">Client</th>
              <th className="px-3 py-2 text-left font-medium">Default</th>
              <th className="px-3 py-2 text-right font-medium">Tasks</th>
              <th className="px-3 py-2 text-right font-medium">Budget</th>
            </tr>
          </thead>
          <tbody>
            {projects.isPending ? (
              <tr>
                <td colSpan={5} className="p-3">
                  <Skeleton className="h-24 w-full" />
                </td>
              </tr>
            ) : visibleRows.length ? (
              visibleRows.map((row) => (
                <tr
                  key={row.id}
                  className="cursor-pointer border-b last:border-b-0 hover:bg-muted/40"
                  onClick={() => setPanel(row.id)}
                >
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      className="text-left font-medium outline-none focus-visible:underline"
                      onClick={(event) => {
                        event.stopPropagation()
                        setPanel(row.id)
                      }}
                    >
                      {row.name}
                    </button>
                    {row.code && (
                      <span className="ml-2 font-mono text-xs text-muted-foreground">
                        {row.code}
                      </span>
                    )}
                    {!row.active && (
                      <Badge variant="secondary" className="ml-2">
                        Inactive
                      </Badge>
                    )}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {row.customerId
                      ? (customerNames.get(row.customerId) ?? 'Unknown client')
                      : 'Internal'}
                  </td>
                  <td className="px-3 py-2">
                    <Badge variant="outline">{row.billable ? 'Billable' : 'Non-billable'}</Badge>
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums">
                    {taskCounts.get(row.id) ?? 0}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap text-muted-foreground tabular-nums">
                    {row.budgetMinutes === null
                      ? '–'
                      : `${formatDecimalHours(row.budgetMinutes)} h`}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} className="px-3 py-10 text-center text-muted-foreground">
                  {projects.data?.length
                    ? 'No projects match.'
                    : 'No projects yet. Add the first one.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <ProjectPanel
        open={Boolean(panel) && (panel === 'new' || Boolean(editing))}
        project={editing}
        onOpenChange={(open) => {
          if (!open) setPanel(null)
        }}
        onCreated={(project) => setPanel(project.id)}
      />
    </PageBody>
  )
}
